export type CurriculumLayer = {
  depth: number;
  name: string;
};

export type CurriculumLessonSlot = {
  id: number;
  node_id: number;
  lesson_id: number | null;
  title: string;
  status: "placeholder" | "published" | "retired";
  version_number: number;
  /**
   * The assigned lesson's own publish flag.
   *
   * `status` above is the curriculum's version bookkeeping and reads
   * "published" for any assigned lesson, draft or not, so it cannot answer
   * this. Null when the slot holds no lesson.
   */
  lesson_is_published?: boolean | null;
};

export type CurriculumNode = {
  id: number;
  parent_id: number | null;
  depth: number;
  name: string;
  sort_order: number;
  is_lesson: boolean;
  lessons: CurriculumLessonSlot[];
};

export type CurriculumOrganization = {
  id: string;
  name: string | null;
};

export type CurriculumSummary = {
  id: number;
  name: string;
  logo_url: string | null;
  organization_count: number;
  layer_count: number;
  lesson_count: number;
  placeholder_count: number;
  revision: number;
  updated_at: string;
  can_edit: boolean;
};

export type OrganizationTakenElsewhere = {
  id: string;
  name: string | null;
  curriculum_id: number;
  curriculum_name: string;
};

export type CurriculumDetail = CurriculumSummary & {
  created_by_user_id: number | null;
  layers: CurriculumLayer[];
  organizations: CurriculumOrganization[];
  nodes: CurriculumNode[];
  /**
   * Organizations already bound to a different curriculum.
   *
   * One organization follows one curriculum for now, so these are not offered
   * as options. Optional so a dashboard ahead of the backend simply offers
   * everything rather than breaking.
   */
  organizations_taken_elsewhere?: OrganizationTakenElsewhere[];
};

export type CurriculumNodeInput = {
  id: number | null;
  parent_id: number | null;
  name: string;
  sort_order: number;
  children: CurriculumNodeInput[];
  lessons: {
    id: number | null;
    lesson_id: number | null;
    title: string;
    status: "placeholder" | "published";
  }[];
};

export type CurriculumStructureRequest = {
  revision: number;
  layers: CurriculumLayer[];
  nodes: CurriculumNodeInput[];
  /**
   * Nodes the editor accepts losing.
   *
   * A structure save is a full replacement: any node the payload omits is
   * unassigned. The API refuses that silently only for drafts -- a node that is
   * published or already on a student's schedule has to be named here, so a
   * truncated payload fails loudly instead of destroying assigned work. Left
   * empty until the editor has shown the removals and the user has confirmed.
   */
  confirm_removed_node_ids?: number[];
};

/** One node a structure save would drop, from the confirmation 422. */
export type CurriculumRemovalNode = {
  id: number;
  name: string | null;
  published: boolean;
  scheduled: boolean;
};

export type CurriculumRemovalConfirmation = {
  message: string;
  code: "curriculum_removals_need_confirmation";
  nodes: CurriculumRemovalNode[];
};

export type CurriculumRequestError = Error & {
  status?: number;
  /** Set when the API asked for removals to be confirmed and resent. */
  removals?: CurriculumRemovalConfirmation;
};

export type CurriculumLogoPresign = {
  upload_url: string;
  object_key: string;
  headers: Record<string, string>;
};

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(`/api/proxy/curriculum${path}`, {
    credentials: "include",
    // The proxy sets no cache headers, so a plain GET is open to the browser's
    // heuristic freshness. A curriculum list served from that cache shows the
    // organization counts from before the last assignment was saved.
    cache: "no-store",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      detail?: string | CurriculumRemovalConfirmation;
      error?: string;
    };
    // A FastAPI detail is not always a string. Reading one straight into
    // `new Error(...)` produced "[object Object]" and threw the payload away,
    // so a structured detail has to be unpacked rather than stringified.
    const structured =
      typeof body.detail === "object" && body.detail !== null
        ? body.detail
        : null;
    const error = new Error(
      structured?.message ||
        (typeof body.detail === "string" ? body.detail : "") ||
        body.error ||
        `Curriculum request failed (${response.status})`,
    ) as CurriculumRequestError;
    error.status = response.status;
    if (structured?.code === "curriculum_removals_need_confirmation") {
      error.removals = structured;
    }
    throw error;
  }

  return response.json() as Promise<T>;
};

export const curriculumService = {
  list: () => request<CurriculumSummary[]>(""),
  get: (curriculumId: number) =>
    request<CurriculumDetail>(`/${encodeURIComponent(curriculumId)}`),
  create: (name: string) =>
    request<CurriculumDetail>("", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  rename: (curriculumId: number, name: string) =>
    request<CurriculumDetail>(`/${encodeURIComponent(curriculumId)}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  updateStructure: (curriculumId: number, body: CurriculumStructureRequest) =>
    request<CurriculumDetail>(
      `/${encodeURIComponent(curriculumId)}/structure`,
      { method: "PUT", body: JSON.stringify(body) },
    ),
  updateOrganizations: (curriculumId: number, organizationIds: string[]) =>
    request<CurriculumDetail>(
      `/${encodeURIComponent(curriculumId)}/organizations`,
      { method: "PUT", body: JSON.stringify({ organization_ids: organizationIds }) },
    ),
  presignLogo: (curriculumId: number, body: { content_type: "image/png" | "image/jpeg" | "image/webp"; size: number; checksum: string }) =>
    request<CurriculumLogoPresign>(`/${encodeURIComponent(curriculumId)}/logo/presign`, { method: "POST", body: JSON.stringify(body) }),
  completeLogo: (curriculumId: number, body: { object_key: string; checksum: string }) =>
    request<CurriculumDetail>(`/${encodeURIComponent(curriculumId)}/logo/complete`, { method: "POST", body: JSON.stringify(body) }),
  deleteLogo: (curriculumId: number) =>
    request<CurriculumDetail>(`/${encodeURIComponent(curriculumId)}/logo`, { method: "DELETE" }),
};

export const CURRICULUM_LOGO_CONTENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
] as const;

export type CurriculumLogoContentType =
  (typeof CURRICULUM_LOGO_CONTENT_TYPES)[number];

/** The size the API refuses at logo/complete, checked before the upload. */
export const CURRICULUM_LOGO_MAX_BYTES = 5 * 1024 * 1024;

export const isCurriculumLogoContentType = (
  value: string,
): value is CurriculumLogoContentType =>
  (CURRICULUM_LOGO_CONTENT_TYPES as readonly string[]).includes(value);

/**
 * Presign, upload to storage, then register the object on the curriculum.
 *
 * One function because two screens do this — the create dialog and the
 * curriculum header — and a second copy is where the two drift apart.
 *
 * Type and size are checked here rather than at either call site. The API
 * refuses both, but only at logo/complete, which is after the file has already
 * been uploaded and whose refusal names neither reason.
 */
export async function uploadCurriculumLogo(
  curriculumId: number,
  file: File,
): Promise<CurriculumDetail> {
  if (!isCurriculumLogoContentType(file.type)) {
    throw new Error("Use PNG, JPEG, or WebP for curriculum logos");
  }
  if (file.size > CURRICULUM_LOGO_MAX_BYTES) {
    throw new Error("Curriculum logos are limited to 5 MB");
  }

  const checksum = await sha256Checksum(file);
  const presign = await curriculumService.presignLogo(curriculumId, {
    content_type: file.type,
    size: file.size,
    checksum,
  });
  // Straight to object storage, so a failure here is the upload itself rather
  // than the dashboard's API. The status is carried into the message: without
  // it, a bucket that rejects the PUT reads the same as one that is
  // unreachable, and neither says which.
  const upload = await fetch(presign.upload_url, {
    method: "PUT",
    headers: presign.headers,
    body: file,
  });
  if (!upload.ok) {
    throw new Error(`Logo upload failed (${upload.status})`);
  }

  return curriculumService.completeLogo(curriculumId, {
    object_key: presign.object_key,
    checksum,
  });
}

export async function sha256Checksum(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function listCurricula(): Promise<CurriculumSummary[]> {
  return request<CurriculumSummary[]>("");
}
